import * as React from 'react';
import { StyleSheet, View } from 'react-native';
import { useHistory, useLocation } from 'react-router-dom';
import { signIn } from 'aws-amplify/auth';

import * as jose from 'jose';

import { getLocalizedTexts } from '../../Locales';
import color from '../../style/color';
import { useWindowSize } from '../../style/utils';
import { getSocialIdentityCredentials, GetSocialIdentityCredentialsResponse, Provider } from '../../api/rest/user';
import { logAmplitudeEvent } from '../../lib/events/amplitudeEvents';
import LoadingFrame from '../../components/auth/LoadingFrame';
import IntroPanel from './IntroPanel';
import SocialSignUpPage from './SocialSignUpPage';
import {
    OAUTH_PROVIDERS,
    beginOAuthRequest,
    consumeOAuthRequest,
    validateOAuthNonce,
} from '../../lib/auth/oauthRequestSecurity';

const APPLE_OAUTH_URL = 'https://appleid.apple.com/auth/authorize';
const APPLE_CLIENT_ID = 'io.wylr.joko-web';

const LOCALHOST_REDIRECT_URL = 'https://joko.com/apple-login';

function AppleAuthPage() {
    const texts = getLocalizedTexts().auth.signIn.withApple;
    const size = useWindowSize();
    const [signUpData, errorMessage] = useHandleRedirection();
    if (!signUpData)
        return (
            <View style={styles.container}>
                <LoadingFrame
                    text={errorMessage || texts.loading}
                    height={(size.height || 0) - 120}
                    width={(size.width || 0) - 240}
                    isError={!!errorMessage}
                />
            </View>
        );
    else
        return (
            <View style={styles.containerSignUp}>
                <View style={styles.containerSignUpColumn}>
                    <IntroPanel />
                </View>
                <View style={styles.containerSignUpColumn}>
                    <SocialSignUpPage {...signUpData} />
                </View>
            </View>
        );
}

export default AppleAuthPage;

function useHandleRedirection(): [SignUpData | undefined, string | undefined] {
    const location = useLocation();
    const redirectUri =
        window.location.host === 'localhost:3000'
            ? LOCALHOST_REDIRECT_URL
            : `https://${window.location.host}${location.pathname}`;
    const [handleOAuthResponse, handleOAuthFailure, signUpData, errorMessage] = useHandleOAuthResponse();
    React.useEffect(() => {
        const responseParameters = new URLSearchParams(location.hash.substring(1));
        const idToken = responseParameters.get('id_token');
        if (idToken) {
            handleOAuthResponse(idToken, responseParameters.get('state'));
        } else {
            if (window.location.host === 'localhost:3000')
                alert(`In order to test Apple sign-in in Localhost, replace "${LOCALHOST_REDIRECT_URL}" with this page URL`);
            try {
                const request = beginOAuthRequest(OAUTH_PROVIDERS.APPLE, true);
                if (request.nonce === undefined) throw new Error('Apple OAuth nonce missing');
                const authorizationUrl = new URL(APPLE_OAUTH_URL);
                authorizationUrl.searchParams.set('client_id', APPLE_CLIENT_ID);
                authorizationUrl.searchParams.set('redirect_uri', redirectUri);
                authorizationUrl.searchParams.set('response_type', 'code id_token');
                authorizationUrl.searchParams.set('state', request.state);
                authorizationUrl.searchParams.set('nonce', request.nonce);
                authorizationUrl.searchParams.set('response_mode', 'fragment');
                window.open(authorizationUrl.toString(), window.location.host === 'localhost:3000' ? undefined : '_self');
            } catch (error) {
                handleOAuthFailure(error);
            }
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    return [signUpData, errorMessage];
}

interface SignUpData {
    initialEmail: string;
    credentials: GetSocialIdentityCredentialsResponse;
}

function useHandleOAuthResponse(): [
    (idToken: string, returnedState: string | null) => void,
    (error: unknown) => void,
    SignUpData | undefined,
    string | undefined
] {
    const texts = getLocalizedTexts().auth.signIn.withApple;
    const history = useHistory();
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);
    const [signUpData, setSignUpData] = React.useState<SignUpData | undefined>(undefined);
    const handleOAuthFailure = (error: unknown) => {
        console.log(error);
        setErrorMessage(texts.error.default);
        logAmplitudeEvent({ name: 'Registration - Apple Login Failed' });
        setTimeout(() => history.push('/auth'), 1000);
    };
    const handleOAuthResponse = async (idToken: string, returnedState: string | null) => {
        try {
            const { expectedNonce } = consumeOAuthRequest(OAUTH_PROVIDERS.APPLE, returnedState, true);
            if (expectedNonce === undefined) throw new Error('Apple OAuth nonce missing');
            const verifiedToken = await verifyAppleJWTToken(idToken, expectedNonce);
            // Apple doesn't provide an email in the idToken for Work & School users, so we use a fall back for the rest of the flow. For more information see:
            // https://developer.apple.com/documentation/sign_in_with_apple/sign_in_with_apple_rest_api/authenticating_users_with_sign_in_with_apple#3383773
            const appleEmail = verifiedToken.email || `${verifiedToken.sub}@apple.com`;
            const credentials = await getSocialIdentityCredentials({
                provider: Provider.Apple,
                providerUserId: verifiedToken.sub,
                email: appleEmail,
                secretToken: idToken,
            });
            if (!credentials.new) await handleSignIn(credentials);
            else setSignUpData({ initialEmail: appleEmail, credentials });
        } catch (error) {
            handleOAuthFailure(error);
        }
    };
    return [handleOAuthResponse, handleOAuthFailure, signUpData, errorMessage];
}

const APPLE_PUBLIC_KEYS_URL = 'https://appleid.apple.com/auth/keys';

async function verifyAppleJWTToken(JWTToken: string, expectedNonce: string): Promise<{ sub: string; email?: string }> {
    console.log('Apple Sign-in: verifying JWT token...');
    // Create a remote JWK set that fetches keys from Apple's JWKS endpoint
    const JWKS = jose.createRemoteJWKSet(new URL(APPLE_PUBLIC_KEYS_URL));
    // Verify the JWT using the remote JWKS
    const { payload } = await jose.jwtVerify(JWTToken, JWKS, {
        algorithms: ['RS256'],
        issuer: 'https://appleid.apple.com',
        audience: APPLE_CLIENT_ID,
    });
    console.log('Apple Sign-in: JWT token verified');
    if (!payload || !payload.sub || typeof payload.sub !== 'string')
        throw new Error(`Apple Sign-in: missing 'sub' key in verified JWT Token`);
    validateOAuthNonce(
        OAUTH_PROVIDERS.APPLE,
        expectedNonce,
        typeof payload.nonce === 'string' ? payload.nonce : undefined
    );
    return payload as { sub: string; email?: string };
}

async function handleSignIn(credentials: GetSocialIdentityCredentialsResponse) {
    await signIn({
        username: credentials.userId,
        password: credentials.secretKey,
        options: { authFlowType: 'CUSTOM_WITH_SRP' },
    });
    logAmplitudeEvent({ name: 'Registration - Apple Login Succeeded' });
    window.location.reload();
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        marginTop: 60,
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: color.white,
    },
    containerSignUp: {
        flex: 1,
        flexDirection: 'row',
    },
    containerSignUpColumn: {
        flex: 1,
    },
});
